import * as React from 'react'
import Image from 'next/image'

import { Stack, Grid, useMediaQuery, Theme } from '@mui/material'
import { styled } from '@mui/material/styles'

import { I18n, useTranslation } from 'components/hooks/use-translation'
import Icon from 'components/atomic/atoms/icon'
import Text from 'components/atomic/atoms/text'

const ImageWrapper = styled('div')`
    position: relative;
    min-height: 15rem;
    min-width: 25rem;
    transform: translateY(1.5rem);
    opacity: 0.7;
    flex: 1;

    ${({ theme }) => theme.breakpoints.up('md')} {
        min-height: 19rem;
    }
`

const CtaCard = styled('div')`
    height: 100%;
    padding: 1.5rem;
    border-radius: ${({ theme }) => theme.shape.borderRadius}px;
    border: ${({ theme }) => `1px solid ${theme.palette.divider}`};
    overflow: hidden;
`

const Feature = (props: { title: string; description: string; image: string; fullWidth: boolean }) => {
    const isSM = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))
    const fullWidth = props.fullWidth && !isSM

    return (
        <Grid item xs>
            <CtaCard>
                <Stack
                    direction={fullWidth ? 'row' : 'column'}
                    spacing={2}
                    justifyContent="space-between"
                    height="100%">
                    <Stack flex={1} spacing="1rem">
                        <Text variant="h5">{props.title}</Text>
                        <Text variant="body1" color="text.secondary">
                            {props.description}
                        </Text>
                    </Stack>
                    <ImageWrapper>
                        <Image
                            src={props.image}
                            layout="fill"
                            objectFit="contain"
                            objectPosition="bottom"
                            alt={props.description}
                        />
                    </ImageWrapper>
                </Stack>
            </CtaCard>
        </Grid>
    )
}

const MethodSection = () => {
    const { t }: I18n = useTranslation()

    const features = [
        {
            image: '/assets/devv-method/1.png',
            title: t('path:methodSection.feature1Title'),
            description: t('path:methodSection.feature1'),
        },
        {
            image: '/assets/devv-method/2.png',
            title: t('path:methodSection.feature2Title'),
            description: t('path:methodSection.feature2'),
        },
        {
            image: '/assets/devv-method/3.png',
            title: t('path:methodSection.feature3Title'),
            description: t('path:methodSection.feature3'),
        },
    ]

    return (
        <div>
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <Icon fontSize="small" color="primary" name="taskAliIcon" />
                <Text sx={{ textTransform: 'uppercase', fontWeight: 'bold' }} color="primary">
                    {t('path:methodSection.title')}
                </Text>
            </Stack>

            <Text variant="h4">{t('path:methodSection.methodTitle')}</Text>

            <Grid container spacing={2} mt={4}>
                {features.map((item, i) => (
                    <Feature key={item.title} {...item} fullWidth={features.length - 1 === i} />
                ))}
            </Grid>
        </div>
    )
}

export default MethodSection
